import { useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmAction } from '@/components/confirm';
import { Busy } from '@/components/kit';
import CopyButton from '@/components/admin/CopyButton';
import { ORDER_LIST_FIELDS, TEMPLATE_FIELDS } from '@/shared/fields';
import { newCode, RENEW_WARN_DAYS } from '@/shared/pricing';
import { committed, idOf, logActivity, nowIso, rows } from '@/shared/data';
import { orderVars, renderTemplate } from '@/shared/messages';

const { eq, and, gte, lte, lt, isNull, isIn } = window.ApperSDK.CoreSDK;

const DAY = 86400000;
const iso = (d) => d.toISOString().slice(0, 19);

const FALLBACK = {
  Expiry: { subject_c: 'Your line ends {ends}', body_c: 'Hi {name},\n\nYour line on order {code} ends {ends}.\n\nGhost Cable' },
  Winback: { subject_c: 'We kept your seat warm', body_c: 'Hi {name},\n\nUse {discount} for 15% off any plan.\n\nGhost Cable' },
};

async function sendReminders() {
  const now = new Date();
  const soon = new Date(now.getTime() + RENEW_WARN_DAYS * DAY);

  const [expiring, lapsed, customers, templates] = await Promise.all([
    sdk
      .table('orders_c')
      .select(ORDER_LIST_FIELDS)
      .where(and(eq('status_c', 'Active'), gte('ends_at_c', iso(now)), lte('ends_at_c', iso(soon)), isNull('expiry_emailed_at_c')))
      .limit(100)
      .fetch()
      .then(rows),
    sdk
      .table('orders_c')
      .select(ORDER_LIST_FIELDS)
      .where(and(eq('status_c', 'Active'), lt('ends_at_c', iso(now)), isNull('winback_emailed_at_c')))
      .limit(100)
      .fetch()
      .then(rows),
    sdk
      .table('customers_c')
      .select(['Name', 'email_c', 'expiry_reminders_c', 'marketing_opt_in_c', 'unsubscribed_at_c'])
      .limit(500)
      .fetch()
      .then(rows),
    sdk
      .table('templates_c')
      .select(TEMPLATE_FIELDS)
      .where(isIn('kind_c', ['Expiry', 'Winback']))
      .limit(20)
      .fetch()
      .then(rows),
  ]);

  const byId = new Map(customers.map((c) => [String(c.Id), c]));
  const customerOf = (o) => byId.get(String(idOf(o.customer_c)));
  const tpl = (kind) => templates.find((t) => t.kind_c === kind) ?? FALLBACK[kind];
  const messages = [];
  const stamp = nowIso();

  const toRemind = expiring.filter((o) => customerOf(o)?.expiry_reminders_c !== false);
  if (toRemind.length) {
    const res = await sdk.table('orders_c').update(toRemind.map((o) => ({ Id: o.Id, expiry_emailed_at_c: stamp })));
    if (committed(res)) {
      toRemind.forEach((o) => {
        messages.push({ kind: 'Expiry', to: o.email_c, order: o.Name, ...renderTemplate(tpl('Expiry'), orderVars(o)) });
      });
    }
  }

  const toWin = lapsed.filter((o) => {
    const c = customerOf(o);
    return !c || (c.marketing_opt_in_c !== false && !c.unsubscribed_at_c);
  });
  if (toWin.length) {
    const ends = new Date(now.getTime() + 14 * DAY).toISOString();
    const codes = toWin.map(() => newCode('BACK-', 6));
    const made = await sdk.table('discount_codes_c').create(
      toWin.map((o, i) => ({
        Name: codes[i],
        kind_c: 'Percent',
        value_c: 15,
        plan_c: 'All',
        max_uses_c: 1,
        uses_c: 0,
        expires_at_c: ends,
        active_c: true,
        customer_c: idOf(o.customer_c),
        credit_used_c: 0,
      })),
    );
    if (committed(made)) {
      const res = await sdk.table('orders_c').update(toWin.map((o) => ({ Id: o.Id, winback_emailed_at_c: stamp })));
      if (committed(res)) {
        toWin.forEach((o, i) => {
          messages.push({
            kind: 'Win-back',
            to: o.email_c,
            order: o.Name,
            ...renderTemplate(tpl('Winback'), orderVars(o, { discount: codes[i] })),
          });
        });
      }
    }
  }

  const expiryCount = messages.filter((m) => m.kind === 'Expiry').length;
  const winCount = messages.length - expiryCount;
  await logActivity('Email', 'Ran reminders', '', `${expiryCount} expiry, ${winCount} win-back`);
  return messages;
}

const SEND_EMAIL = import.meta.env.VITE_SEND_EMAIL;

/** One reminder through the send-email function. Resolves { ok, message }. */
async function sendOne(m) {
  try {
    const r = await sdk.functions.invoke(SEND_EMAIL, { mode: 'mail', to: m.to, subject: m.subject, text: m.body });
    const v = r?.value;
    if (!r?.ok || v?.success === false || v?.error || !v?.sent || v?.partial) {
      const message = v?.message || (typeof v?.error === 'string' ? v.error : v?.error?.message) || r?.error?.message;
      return { ok: false, message: message || 'Not sent' };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || 'Not sent' };
  }
}

/** Owner-only button: stamps expiry and win-back reminders, then sends them or hands them back to copy. */
export default function Reminders({ onDone }) {
  const [results, setResults] = useState(null);
  const [status, setStatus] = useState({});
  const [sending, setSending] = useState(false);

  const keyOf = (m, i) => `${m.order}-${m.kind}-${i}`;
  const pending = (results ?? []).filter((m, i) => !status[keyOf(m, i)]?.ok && m.to);

  async function sendAll() {
    if (!results?.length) return;
    setSending(true);
    let sent = 0;
    let failed = 0;
    let firstError = '';
    try {
      for (let i = 0; i < results.length; i += 1) {
        const m = results[i];
        const k = keyOf(m, i);
        if (status[k]?.ok || !m.to) continue;
        setStatus((s) => ({ ...s, [k]: { busy: true } }));
        const out = await sendOne(m);
        setStatus((s) => ({ ...s, [k]: out }));
        if (out.ok) sent += 1;
        else {
          failed += 1;
          firstError = firstError || out.message;
          // A settings problem fails every message the same way, so stop early.
          if (/not set up|not finished|sign-in|could not be read|blocked/i.test(out.message || '') && sent === 0) break;
        }
      }
    } finally {
      setSending(false);
    }
    if (sent) logActivity('Email', 'Sent reminders', '', `${sent} sent${failed ? `, ${failed} failed` : ''}`);
    if (failed) toast.error(`${sent} sent, ${failed} not sent`, { description: firstError });
    else if (sent) toast.success(`${sent} reminder${sent === 1 ? '' : 's'} sent`);
  }

  function close() {
    if (sending) return;
    setResults(null);
    setStatus({});
  }

  async function go() {
    try {
      const out = await sendReminders();
      setResults(out);
      toast.success(out.length ? `${out.length} reminder${out.length === 1 ? '' : 's'} ready` : 'Nobody is due a reminder');
      if (onDone) onDone();
      return true;
    } catch (e) {
      toast.error(e?.message || 'Reminders failed');
      return false;
    }
  }

  return (
    <>
      <ConfirmAction
        label="Run reminders"
        icon="BellRing"
        title="Run tonight's reminders?"
        description="Lines ending within 7 days get an expiry reminder. Lines that lapsed get a one-use 15% BACK code valid for 14 days. Each order is only reminded once."
        confirmLabel="Run reminders"
        busyLabel="Running…"
        onConfirm={go}
      />
      <Dialog open={results !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent className="max-h-[90svh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reminders</DialogTitle>
            <DialogDescription>
              Send them all through your email settings, or copy any one and send it from your inbox.
            </DialogDescription>
          </DialogHeader>
          {results?.length ? (
            <ul className="space-y-3">
              {results.map((m, i) => {
                const st = status[keyOf(m, i)];
                return (
                  <li key={keyOf(m, i)} className="rounded-md border border-border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="min-w-0 text-sm">
                        <span className="font-medium">{m.kind}</span>
                        <span className="text-muted-foreground"> · {m.to} · </span>
                        <span className="font-mono">{m.order}</span>
                      </p>
                      <div className="flex items-center gap-2">
                        {st?.busy && <ApperIcon name="LoaderCircle" size={16} className="animate-spin text-muted-foreground" />}
                        {st?.ok && (
                          <span className="inline-flex items-center gap-1 text-xs text-success">
                            <ApperIcon name="CircleCheck" size={14} />
                            Sent
                          </span>
                        )}
                        {st && !st.ok && !st.busy && (
                          <span className="inline-flex items-center gap-1 text-xs text-destructive" title={st.message}>
                            <ApperIcon name="CircleAlert" size={14} />
                            Not sent
                          </span>
                        )}
                        <CopyButton text={`${m.subject}\n\n${m.body}`} />
                      </div>
                    </div>
                    <p className="mt-2 text-sm font-medium">{m.subject}</p>
                    <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-sm text-muted-foreground">{m.body}</pre>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="flex items-center gap-2 rounded-md border border-border p-4 text-sm text-muted-foreground">
              <ApperIcon name="MoonStar" size={16} className="text-primary" />
              Nobody is due a reminder tonight.
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant={pending.length ? 'outline' : 'default'} onClick={close} disabled={sending}>
              Done
            </Button>
            {pending.length > 0 && (
              <Button type="button" onClick={sendAll} disabled={sending}>
                <Busy
                  busy={sending}
                  label={
                    <>
                      <ApperIcon name="Send" size={16} />
                      Send {pending.length === 1 ? 'it' : `all ${pending.length}`}
                    </>
                  }
                  busyLabel="Sending…"
                />
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
